import { Container, Graphics } from 'pixi.js';
import { COLORS } from '@/config/colors';
import { label } from './widgets';

/** Seconds a "+N" gain bloom stays on screen */
const GAIN_LIFE = 0.85;
const GAIN_RISE = 26;

/**
 * Buy feedback (docs/12 #1): a "+N" with an electronic charge ring blooms
 * from the header HP/armor/yellow readout (repair, armor, scrap gems).
 * Lives on a persistent overlay — the bay's rebuild never clears it.
 */
export type GainKind = 'armor' | 'hp' | 'gems';
export type GainAnchors = Readonly<Record<GainKind, { readonly x: number; readonly y: number }>>;

/** Header readout anchors (design space, v4 vitals positions) */
const DESKTOP_ANCHORS: GainAnchors = {
  armor: { x: 600, y: 39 }, hp: { x: 300, y: 39 }, gems: { x: 1130, y: 39 },
};

export class ShopGains {
  readonly view = new Container();
  private gains: { kind: GainKind; amount: number; t: number }[] = [];
  private anchors: GainAnchors = DESKTOP_ANCHORS;

  /** Where the blooms rise from (null = the desktop header). */
  setAnchors(anchors: GainAnchors | null): void {
    this.anchors = anchors ?? DESKTOP_ANCHORS;
  }

  flash(kind: GainKind, amount: number): void {
    this.gains.push({ kind, amount, t: 0 });
  }

  /** Per-frame tick (driven by the game loop while the bay is open). */
  tick(dt: number): void {
    if (this.gains.length === 0) {
      if (this.view.children.length) this.view.removeChildren();
      return;
    }
    for (const g of this.gains) g.t += dt;
    this.gains = this.gains.filter((g) => g.t < GAIN_LIFE);
    this.draw();
  }

  private draw(): void {
    this.view.removeChildren().forEach((c) => c.destroy({ children: true }));
    for (const g of this.gains) {
      const p = Math.min(1, g.t / GAIN_LIFE);
      const rise = 1 - (1 - p) * (1 - p); // ease-out
      const col = g.kind === 'armor' ? COLORS.hudArmor : g.kind === 'gems' ? COLORS.gemYellow : COLORS.hudHp;
      const node = new Container();
      // Electronic charge ring — expands and fades as it "charges in"
      const ring = new Graphics();
      ring.circle(0, 0, 7 + rise * 24).stroke({
        color: col, width: 3 * (1 - p), alpha: 0.75 * (1 - p),
      });
      ring.circle(0, 0, 3 + (1 - rise) * 16).stroke({
        color: col, width: 2, alpha: 0.5 * (1 - p),
      });
      node.addChild(ring);
      const tag = label(`+${g.amount}`, 17, col, 1);
      tag.anchor.set(0.5);
      tag.alpha = 1 - Math.max(0, (p - 0.55) / 0.45);
      node.addChild(tag);
      const at = this.anchors[g.kind];
      node.position.set(at.x, at.y - rise * GAIN_RISE);
      this.view.addChild(node);
    }
  }
}
